import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { useEffect } from "react";
import { useDebounce } from "use-debounce";
import { useGetOrganization } from "~/api/organizations/organizations";
import { customInstance } from "~/custom-instance";
import { useChatDocumentParams } from "~/helpers";
import { trackEvent } from "~/utils/analytics";
import logger from "~/utils/logger";
import { useSelectedEntityStore } from "../stores/selectedEntityStore";

const DEBOUNCE_DELAY = 250; // milliseconds
const STALE_TIME = 10 * 60 * 1000; // 10 minutes
const GC_TIME = 30 * 60 * 1000; // 30 minutes

interface SectionSummaryResponse {
  summary: string;
  truncated: boolean;
  cached: boolean;
}

/**
 * Safely extract error message from various response formats
 */
function getErrorMessage(error: unknown): string {
  if (axios.isAxiosError(error)) {
    const status = error.response?.status;
    const data = error.response?.data;

    if (status === 404) {
      if (typeof data === "object" && data !== null) {
        const errorMsg = (data as Record<string, unknown>).error;
        const message = (data as Record<string, unknown>).message;

        if (typeof errorMsg === "string" && errorMsg) {
          if (errorMsg === "Section not found") {
            return "Section not found for this case";
          }
          if (errorMsg === "No page content available") {
            return "No summary available yet. The document may still be processing.";
          }
          return errorMsg;
        }

        if (typeof message === "string" && message) {
          return message;
        }
      }

      return "Summary not available yet. Please try again later.";
    }

    if (typeof data === "object" && data !== null) {
      const errorMsg = (data as Record<string, unknown>).error;
      const message = (data as Record<string, unknown>).message;

      if (typeof errorMsg === "string" && errorMsg) {
        return errorMsg;
      }
      if (typeof message === "string" && message) {
        return message;
      }
    }

    if (error.message) {
      return error.message;
    }
  }

  if (error instanceof Error) {
    return error.message;
  }

  return "Failed to generate summary";
}

interface UseSectionSummaryResult {
  summary: string | null;
  status: "idle" | "loading" | "success" | "error";
  error: string | null;
  isLoading: boolean;
  isCached: boolean;
  regenerate: () => void;
  canRegenerate: boolean;
  isRegenerating: boolean;
  regenerateError: string | null;
}

async function fetchSummary({
  apiBaseUrl,
  organizationId,
  projectId,
  entityType,
  entityId,
  force,
  signal,
}: {
  apiBaseUrl: string;
  organizationId: string;
  projectId: string;
  entityType: "page" | "section";
  entityId: string;
  force?: boolean;
  signal?: AbortSignal;
}): Promise<SectionSummaryResponse> {
  return customInstance<SectionSummaryResponse>({
    url: `${apiBaseUrl}/organizations/${organizationId}/projects/${projectId}/summary`,
    method: "post",
    data: {
      entityType,
      entityId,
      ...(force ? { force: true } : {}),
    },
    signal,
  });
}

/**
 * Hook for fetching summaries (pages or sections) with optimizations:
 * - Debounces rapid changes (250ms)
 * - Only fetches when Summary tab is active (via enabled prop)
 * - Caches results with React Query (10 min stale time)
 * - Automatically cancels in-flight requests when entity changes
 * - Uses universal endpoint that handles both pages and sections
 */
export const useSectionSummary = (): UseSectionSummaryResult => {
  const { organizationId, caseId: projectId } = useChatDocumentParams();
  const { data: organization } = useGetOrganization(organizationId);
  const selectedEntity = useSelectedEntityStore(
    (state) => state.selectedEntity
  );

  const [debouncedSelectedEntity] = useDebounce(selectedEntity, DEBOUNCE_DELAY);
  const queryClient = useQueryClient();

  const queryKey = [
    "selectedSectionSummary",
    {
      orgId: organizationId,
      projectId,
      entityType: debouncedSelectedEntity?.entityType,
      entityId: debouncedSelectedEntity?.entityId,
      pageIds: debouncedSelectedEntity?.pageIds.join(",") || "",
      language: organization?.language,
    },
  ];

  // Query for selected section from store
  const {
    data,
    isLoading,
    isError,
    error: queryError,
  } = useQuery<SectionSummaryResponse, Error>({
    queryKey,
    queryFn: async ({ signal }) => {
      if (!debouncedSelectedEntity?.entityId) {
        throw new Error("Selected section entity ID is required");
      }

      const API_BASE_URL = import.meta.env.VITE_BASE_API_URL;
      if (!API_BASE_URL) {
        throw new Error(
          "VITE_BASE_API_URL environment variable is not configured"
        );
      }

      const entityType = debouncedSelectedEntity.entityType;
      const entityId = debouncedSelectedEntity.entityId;

      logger.info(
        `Fetching summary for selected entity: entityType=${entityType}, entityId=${entityId}`
      );

      try {
        const result = await fetchSummary({
          apiBaseUrl: API_BASE_URL,
          organizationId,
          projectId,
          entityType,
          entityId,
          signal,
        });

        logger.info(
          `Summary fetched for selected section ${entityId} (cached: ${result.cached}, truncated: ${result.truncated})`
        );

        return result;
      } catch (error: unknown) {
        const status = axios.isAxiosError(error)
          ? error.response?.status
          : undefined;

        logger.error(`Failed to fetch summary for ${entityType} ${entityId}`, {
          status,
          entityType,
          entityId,
        });

        throw new Error(getErrorMessage(error));
      }
    },
    enabled: !!debouncedSelectedEntity?.entityId,
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
    retry: 1,
    refetchOnWindowFocus: false,
  });

  interface RegenerateVariables {
    queryKey: readonly unknown[];
    entityType: "page" | "section";
    entityId: string;
  }

  const {
    mutate: regenerateMutate,
    isPending: isRegenerating,
    isError: isMutationError,
    error: mutationError,
    reset: resetMutation,
  } = useMutation<SectionSummaryResponse, Error, RegenerateVariables>({
    mutationFn: async ({ entityType, entityId }) => {
      const API_BASE_URL = import.meta.env.VITE_BASE_API_URL;
      if (!API_BASE_URL) {
        throw new Error(
          "VITE_BASE_API_URL environment variable is not configured"
        );
      }

      logger.info(
        `Regenerating summary for selected entity: entityType=${entityType}, entityId=${entityId}`
      );

      try {
        const result = await fetchSummary({
          apiBaseUrl: API_BASE_URL,
          organizationId,
          projectId,
          entityType,
          entityId,
          force: true,
        });

        logger.info(
          `Summary regenerated for selected section ${entityId} (cached: ${result.cached}, truncated: ${result.truncated})`
        );

        return result;
      } catch (error: unknown) {
        const status = axios.isAxiosError(error)
          ? error.response?.status
          : undefined;

        logger.error(
          `Failed to regenerate summary for ${entityType} ${entityId}`,
          { status, entityType, entityId }
        );

        throw new Error(getErrorMessage(error));
      }
    },
    // Use the queryKey captured at mutate() invocation time so the result is
    // always written to the cache entry for the entity that was regenerated,
    // even if the user navigates to a different section before this resolves.
    onSuccess: (result, variables) => {
      queryClient.setQueryData(variables.queryKey, result);
    },
  });

  // Clear any prior mutation error when the selected entity changes so a
  // failed regenerate on section A never bleeds into section B's UI.
  useEffect(() => {
    resetMutation();
  }, [
    debouncedSelectedEntity?.entityType,
    debouncedSelectedEntity?.entityId,
    resetMutation,
  ]);

  const regenerate = () => {
    if (!debouncedSelectedEntity?.entityId) return;
    // Track against the debounced entity so the analytics identity always
    // matches the entity the mutation actually regenerates, even during
    // the debounce window after switching sections.
    trackEvent({
      eventName: "app/doc_sidebar/summary/regenerate.button/click",
      properties: {
        entityType: debouncedSelectedEntity.entityType,
        entityId: debouncedSelectedEntity.entityId,
      },
    });
    regenerateMutate({
      queryKey,
      entityType: debouncedSelectedEntity.entityType,
      entityId: debouncedSelectedEntity.entityId,
    });
  };

  // Regeneration is a *mutation* that runs while the query's data remains
  // valid. Keeping status based solely on the query means the previously
  // loaded summary and the toolbar stay mounted while `isRegenerating` is
  // true; the button surfaces its own in-progress state via `isRegenerating`.
  // Similarly, a failed regeneration does not move the view into the error
  // state — it is surfaced separately via `regenerateError` so the user can
  // retry without losing the existing summary.
  let status: "idle" | "loading" | "success" | "error" = "idle";
  if (isLoading) {
    status = "loading";
  } else if (isError) {
    status = "error";
  } else if (data) {
    status = "success";
  }

  const error = isError
    ? queryError?.message || "Failed to generate summary"
    : null;
  const regenerateError = isMutationError
    ? mutationError?.message || "Failed to regenerate summary"
    : null;

  // Derive canRegenerate from the debounced entity so the button's enabled
  // state tracks the same entity the mutation will actually operate on,
  // preventing a race where a click during the 250ms debounce window would
  // regenerate the previous section.
  const canRegenerate =
    !!debouncedSelectedEntity?.entityId && !isRegenerating && !isLoading;

  return {
    summary: data?.summary || null,
    status,
    error,
    isLoading,
    isCached: data?.cached || false,
    regenerate,
    canRegenerate,
    isRegenerating,
    regenerateError,
  };
};
